import { FieldProps } from "formik"
import { toJS } from "mobx"
import { observer } from "mobx-react-lite"
import React, { useEffect, useMemo, useState } from "react"
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List,
  ListProps,
} from "react-virtualized"

import Dropdown from "@components/prototypes/FilterSidebar/components/Dropdown/Dropdown"
import CheckboxWithLabel from "@components/ui/Checkbox/CheckboxWithLabel"
import ListItem from "@components/ui/ListItem/ListItem"
import Loader from "@components/ui/Loader/BarLoader"
import NotFound from "@components/ui/NotFound/NotFound"
import Templates from "@components/ui/Templates"
import Text from "@components/ui/Typography/Text"
import { useController, useStore } from "@store"
import { Company } from "@store/product/product.store"
import { searchBy } from "@utils/optionsUtils"

import styles from "./ProductFilterModal.module.sass"

interface ProductFilterManufacturerFieldProps extends FieldProps<string[]> {
  label?: string
  field: any
  form: any
  searchQuery: string
}

const ProductFilterManufacturerField: React.FC<ProductFilterManufacturerFieldProps> =
  observer(({ label, field, form, searchQuery }) => {
    const [open, setOpen] = useState(false)

    const { value: fieldValue = [], name } = field
    const { setFieldValue } = form

    const value = [...fieldValue]

    const { contentManagerController } = useController()
    const {
      contentManagerStore: { companies, loadingCompanies },
    } = useStore()

    useEffect(() => {
      contentManagerController.getCompanies()
    }, [])

    const companyCards: Company[] = useMemo(() => {
      return Array.from(
        searchBy(companies, searchQuery, (company) => company.name),
        (company) => toJS(company)
      )
    }, [companies, searchQuery])

    const handleCompanyClick = (newValue: Company) => {
      const foundIdx = value?.findIndex((value) => value.id === newValue.id)
      if (foundIdx === -1) {
        setFieldValue(name, [...value, newValue])
        return
      }
      value.splice(foundIdx, 1)
      setFieldValue(name, value)
    }

    const renderRow: ListProps["rowRenderer"] = ({
      index,
      key,
      parent,
      style,
    }) => {
      const company = companyCards[index]

      if (!company)
        return (
          <div style={style} key={key}>
            <ListItem>
              <Text color="text50Color" variant="inherit">
                loading...
              </Text>
            </ListItem>
          </div>
        )

      const active = !!value?.find((data) => data.id === company.id)

      return (
        <CellMeasurer
          cache={cache}
          columnIndex={0}
          rowIndex={index}
          parent={parent}
          key={`${key}-cached`}
        >
          <div style={style} key={key}>
            <ListItem
              active={active}
              onClick={() => handleCompanyClick(company as Company)}
            >
              <Text>{company.name}</Text>
            </ListItem>
          </div>
        </CellMeasurer>
      )
    }

    const cache = useMemo(() => {
      return new CellMeasurerCache({
        fixedWidth: true,
        minHeight: 42,
      })
    }, [])

    const isChecked = !!value?.length

    const handleUncheck = (e: React.MouseEvent) => {
      if (isChecked) setFieldValue(name, undefined)
      else setOpen(true)
      e.stopPropagation()
    }

    return (
      <Dropdown
        title={label}
        opened={open}
        onClick={() => setOpen((value) => !value)}
        before={
          <CheckboxWithLabel
            checked={!!value?.length}
            onClick={handleUncheck}
          />
        }
      >
        <Templates.RollScript className={styles.root}>
          <div className={styles.container}>
            {companyCards.length === 0 ? (
              loadingCompanies ? (
                <Loader size="large" fluid />
              ) : (
                <NotFound>No Manufacturer found</NotFound>
              )
            ) : (
              <InfiniteLoader
                isRowLoaded={({ index }) => companyCards[index] != null}
                loadMoreRows={async () => {}}
                rowCount={companyCards.length}
                minimumBatchSize={0}
                threshold={0}
              >
                {({ onRowsRendered, registerChild }) => (
                  <AutoSizer>
                    {({ width }) => (
                      <List
                        rowCount={companyCards.length}
                        ref={registerChild}
                        onRowsRendered={onRowsRendered}
                        rowHeight={cache.rowHeight}
                        rowRenderer={renderRow}
                        height={300}
                        width={width}
                      />
                    )}
                  </AutoSizer>
                )}
              </InfiniteLoader>
            )}
          </div>
        </Templates.RollScript>
      </Dropdown>
    )
  })

export default ProductFilterManufacturerField
